import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, getDoc, addDoc, collection, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { chatWithAI } from '../services/aiApiService';
import ReactMarkdown from 'react-markdown';
import './AppletWidget.css';

function AppletWidget({ appletId }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [appletConfig, setAppletConfig] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [profileLanguage, setProfileLanguage] = useState(null);

  const handleCopyToClipboard = async (content, index, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      // Ensure content is a string
      const textToCopy = typeof content === 'string' ? content : String(content);
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        setCopiedMessageIndex(index);
        setTimeout(() => setCopiedMessageIndex(null), 2000);
        return;
      }
      
      // Fallback for older browsers or non-HTTPS contexts
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setCopiedMessageIndex(index);
          setTimeout(() => setCopiedMessageIndex(null), 2000);
        } else {
          console.error('Copy command failed');
        }
      } catch (err) {
        console.error('Failed to copy using fallback method:', err);
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  useEffect(() => {
    loadAppletConfig();
    
    // Get current user email from Firebase auth
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email) {
        setUserEmail(user.email);
      } else {
        setUserEmail('');
      }
    });
    
    return () => unsubscribe();
  }, [appletId]);

  const loadAppletConfig = async () => {
    try {
      const appletDoc = await getDoc(doc(db, 'applets', appletId));
      if (appletDoc.exists()) {
        const data = appletDoc.data();
        // Check if applet is enabled (default to true if not set)
        if (data.enabled === false) {
          setAppletConfig({ ...data, disabled: true });
          return;
        }
        setAppletConfig(data);
        if (data.projectId && data.userId && data.profileId) {
          const profileDoc = await getDoc(doc(db, 'users', data.userId, 'projects', data.projectId, 'profiles', data.profileId));
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            setAppletConfig({ ...data, profile: profileData });
            
            // Load profile's language preference
            if (profileData.language) {
              setProfileLanguage(profileData.language);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading applet config:', error);
    }
  };

  const createConversation = async () => {
    try {
      const conversation = { appletId: appletId, userEmail: userEmail || 'anonymous', messages: [], createdAt: new Date() };
      const docRef = await addDoc(collection(db, 'conversations'), conversation);
      setConversationId(docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  };

  const saveMessage = async (role, content, explicitConversationId = null) => {
    try {
      let currentConversationId = explicitConversationId || conversationId;
      
      // Ensure conversation exists before saving message
      if (!currentConversationId) {
        currentConversationId = await createConversation();
        if (!currentConversationId) {
          console.error('Failed to create conversation');
          return;
        }
        setConversationId(currentConversationId);
      }
      
      const conversationRef = doc(db, 'conversations', currentConversationId);
      const conversationDoc = await getDoc(conversationRef);
      if (conversationDoc.exists()) {
        const currentMessages = conversationDoc.data().messages || [];
        const newMessage = { role, content, timestamp: new Date() };
        await updateDoc(conversationRef, { 
          messages: [...currentMessages, newMessage], 
          updatedAt: new Date() 
        });
      } else {
        console.error('Conversation document does not exist');
      }
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !appletConfig || appletConfig.disabled || appletConfig.enabled === false) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to UI
    const newUserMessage = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      // Ensure conversation exists and get the ID (use local variable to avoid state race condition)
      let currentConversationId = conversationId;
      if (!currentConversationId) {
        currentConversationId = await createConversation();
        if (!currentConversationId) {
          throw new Error('Failed to create conversation');
        }
      }

      // Prepare messages for API
      const apiMessages = [
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user', content: userMessage }
      ];

      // Build prompt with document context and language instruction
      let systemPrompt = appletConfig.profile?.prompt || '';
      
      // Add document context if documents exist
      const documents = appletConfig.profile?.documents || [];
      if (documents.length > 0) {
        // Extract text content from documents
        const documentTexts = documents
          .filter(doc => doc.textContent && doc.textContent.trim())
          .map((doc, index) => {
            return `Document ${index + 1} (${doc.name}):\n${doc.textContent.trim()}`;
          });
        
        if (documentTexts.length > 0) {
          const documentContext = `\n\nYou have access to the following documents that provide context and knowledge. Use this information when answering questions:\n\n${documentTexts.join('\n\n---\n\n')}\n\nWhen answering, reference relevant information from these documents. If the question is not related to the documents, answer using your general knowledge.`;
          systemPrompt = systemPrompt ? systemPrompt + documentContext : `You are a helpful AI assistant with access to the following documents.${documentContext}`;
        }
      }
      
      if (profileLanguage && profileLanguage !== 'auto') {
        // Only add language instruction if a specific language is set (not 'auto')
        const languageInstruction = `\n\nIMPORTANT LANGUAGE REQUIREMENT: You must communicate exclusively in ${profileLanguage}. All your responses, explanations, and interactions must be written in ${profileLanguage}. Even if the user writes in a different language, you should always respond in ${profileLanguage}. Do not switch languages under any circumstances.`;
        systemPrompt = systemPrompt ? systemPrompt + languageInstruction : `You are a helpful AI assistant.${languageInstruction}`;
      }
      // If profileLanguage is 'auto', don't add any language instruction - let AI respond in user's language

      // Call AI API
      const response = await chatWithAI(appletId, apiMessages, {
        model: appletConfig.profile?.model || 'llama-3.1-8b-instruct',
        prompt: systemPrompt,
        parameters: appletConfig.profile?.parameters || {},
        userEmail: userEmail || null
      });

      const aiMessage = { role: 'assistant', content: response.message || response.text || 'Sorry, I could not generate a response.' };
      setMessages(prev => [...prev, aiMessage]);

      // Save both messages to the SAME conversation using the explicit conversationId
      await saveMessage('user', userMessage, currentConversationId);
      await saveMessage('assistant', aiMessage.content, currentConversationId);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { role: 'assistant', content: 'Sorry, there was an error. Please try again.' };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  if (!appletConfig) {
    return (
      <div className="applet-widget">
        <div className="widget-loading">Loading AI agent...</div>
      </div>
    );
  }

  if (appletConfig.disabled || appletConfig.enabled === false) {
    return (
      <div className="applet-widget">
        <div className="widget-disabled">
          <h3>Applet Disabled</h3>
          <p>This applet is currently disabled and cannot be used. Please contact support if it is wrong (info@aidrivenlabs.xyz).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="applet-widget">
      <div className="widget-header">
        <h3>AI Assistant</h3>
      </div>
      <div className="widget-messages">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <p>Hello! How can I help you today?</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div className="message-content">
                {message.role === 'assistant' ? (
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                ) : (
                  message.content
                )}
                <button
                  className="copy-button"
                  onClick={(e) => handleCopyToClipboard(message.content, index, e)}
                  title={copiedMessageIndex === index ? 'Copied!' : 'Copy to clipboard'}
                  type="button"
                >
                  {copiedMessageIndex === index ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.5 2L6 9.5L2.5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="6" y="6" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                      <rect x="2" y="2" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="message assistant">
            <div className="message-content loading">Thinking...</div>
          </div>
        )}
      </div>
      <div className="widget-input">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !loading && !appletConfig.disabled && appletConfig.enabled !== false && handleSendMessage()}
          placeholder="Type your message..."
          disabled={loading || appletConfig.disabled || appletConfig.enabled === false}
        />
        <button onClick={handleSendMessage} disabled={loading || !inputMessage.trim() || appletConfig.disabled || appletConfig.enabled === false}>
          Send
        </button>
      </div>
    </div>
  );
}

export default AppletWidget;

